import { Box, Button, Container, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'
import Markdown from 'react-markdown'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'
import { makeServerProps } from '@/server/query'

import CourseQ from '@/queries/CourseQ'
import { CertificationStatus, LIVE_HELP_TOPIC } from '@/queries/constants'
import SessionQ from '@/queries/SessionQ'
import TopicFollowQ from '@/queries/TopicFollowQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import PageNotFoundError from '@/client/error/PageNotFoundError'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import { getCourseUrlBySlug, getTopicUrlById, getUrl } from '@/client/services/url'
import { isUserDefined } from '@/client/services/user'

import TopicFollowButton from '@/components/Buttons/TopicFollowButton'
import CourseResultsSingleEmpty from '@/components/Course/CourseResults/CourseResultsSingleEmpty'
import CourseTopicsList from '@/components/Course/CourseResults/CourseTopicsList'
import KhanAcademyIcon from '@/components/Icons/KhanAcademyIcon'
import Layout from '@/components/Layout/Layout'
import { useInfiniteScroll } from '@/components/Session/helpers'
import SessionList from '@/components/Session/SessionList'
import ShwLink from '@/components/ShwLink'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'
import TutorCertificationButton from '@/components/Tutor/TutorCertificationButton'

type TopicDetailTab = 'Sessions' | 'Additional Resources'
type TopicSessionsDetailTab = 'Past' | 'Upcoming'

function TopicIndex(): ReactElement {
  const user = useUser()
  const query = useQuery()
  const router = useRouter()
  const renderContext = useAppContext()
  const [queryLimit, handleLoadMore, showAllSessionsPath] = useInfiniteScroll()

  const [currentTab, tabToUrl] = useUrlTabController<TopicDetailTab>({
    queryKey: 'show',
    tabToQueryParam: {
      Sessions: 'sessions',
      'Additional Resources': 'additional-resources',
    },
    defaultTab: 'Sessions',
  })

  const [currentSessionsTab, sessionsTabToUrl] = useUrlTabController<TopicSessionsDetailTab>({
    queryKey: 'filter',
    tabToQueryParam: {
      Upcoming: 'upcoming',
      Past: 'past',
    },
    defaultTab: 'Upcoming',
  })

  const queryTopicId = Number.parseInt(String(router.query.topic), 10)
  const topic = query.one(
    TopicQ.where({
      id: queryTopicId,
      includeInactive: true,
      include: [{ association: 'Course', required: true }],
    })
  )
  const course = topic.one(TopicQ.getCourse())
  const courseSlug = course(CourseQ.slug)
  const courseName = course(CourseQ.name)
  const topicName = topic(TopicQ.name) ?? ''
  const topicId = topic(TopicQ.id)
  const kaUrl = topic(TopicQ.ka_url)
  const isFollowing = topic(TopicQ.isUserFollowing(user.id)) ?? false

  const sessions = topic.many`${SessionQ.where(
    {
      topicId: TopicQ.id,
      order: [[SessionQ.start_date, currentSessionsTab === 'Upcoming' ? 'asc' : 'desc']],
    },
    SQL`
      ${SessionQ.userCanViewSession(user.id)}
      AND ${SessionQ.userMatchesSessionResearchGroup(user)}
      AND ${
        currentSessionsTab === 'Upcoming'
          ? SQL`${SessionQ.getComputedEndDate()} > NOW()`
          : SQL`${SessionQ.getComputedEndDate()} <= NOW()`
      }`
  )}
  LIMIT ${Math.min(queryLimit, 500)}`

  const availableSessionCount = topic<number>`${TopicQ.countSessions(user.id)}`
  const followerCount = topic`COUNT(*) ${TopicFollowQ.where({ topicId: TopicQ.id })}`
  const canUserTutor = (topic(TopicQ.canUserTutor(user.id)) ?? false) && isUserDefined(user)

  const me = query.one(UserQ.where({ id: user.id }))
  const certProfileStatus = me(UserQ.getCertificationProfileStatus()) ?? CertificationStatus.None
  const topicCertStatus =
    topic(TopicQ.getTopicCertificationStatus(user.id)) ?? CertificationStatus.None
  const topicDescription = topic(TopicQ.description) ?? ''
  const topicType = topic(TopicQ.type)
  const certificationTypes = topic(TopicQ.certification_types) ?? []

  if (!renderContext.dryRun && (topicId == null || topicName === LIVE_HELP_TOPIC)) {
    throw new PageNotFoundError('Oh no! This topic does not exist.')
  }

  return (
    <Layout title={topicName} canonical={getTopicUrlById(queryTopicId)}>
      <Box bgColor="bg.subtle">
        <Container className="TopicHeader">
          <Flex alignItems="flex-start" justifyContent="space-between">
            <HStack spacing="10px" mb="16px">
              <ShwLink variant="subtle" href="/explore">
                Explore
              </ShwLink>
              <FontAwesomeIcon icon={faChevronRight} size="sm" color={chakraTokenToVar('fg')} />
              <ShwLink variant="subtle" href={getCourseUrlBySlug(`${courseSlug}`)}>
                {courseName}
              </ShwLink>
              <FontAwesomeIcon icon={faChevronRight} size="sm" color={chakraTokenToVar('fg')} />
              <Text as="span" fontWeight="bold">
                {topicName}
              </Text>
            </HStack>
            <Flex alignItems="center" display={['none', 'auto']}>
              <Text color="fg.muted" mr={2}>
                {pluralize(followerCount, 'Follower')}
              </Text>
            </Flex>
          </Flex>
          <Text variant="eyebrow" mb={2} mt={6}>
            {courseName}
          </Text>
          <Heading as="h1">{topicName}</Heading>
          <Box my="32px" display={['block', 'flex']}>
            <Box mr={[0, '24px']}>
              <TopicFollowButton isFollowing={isFollowing} topicId={topicId ?? 0} />
            </Box>
            <HStack
              className="TopicHeaderButtons"
              spacing="24px"
              display={['none', 'none', 'flex']}
            >
              {kaUrl == null ? null : (
                <ShwLink href={kaUrl} variant="unstyled">
                  <Button
                    variant="outline"
                    leftIcon={
                      <KhanAcademyIcon
                        style={{
                          height: '16px',
                          width: 'auto',
                        }}
                      />
                    }
                  >
                    Practice on Khan Academy
                  </Button>
                </ShwLink>
              )}
              {canUserTutor ? (
                <ShwLink href={getUrl('tutor')}>
                  <Button
                    variant="ghost"
                    leftIcon={<FontAwesomeIcon icon={faCalendar} fontSize="small" />}
                  >
                    Create a Session
                  </Button>
                </ShwLink>
              ) : (
                <TutorCertificationButton
                  inverted
                  certProfileStatus={certProfileStatus}
                  topicCertStatus={topicCertStatus}
                  topicId={topicId ?? 0}
                  topicType={topicType}
                  certificationTypes={certificationTypes}
                />
              )}
            </HStack>
          </Box>
          {topicDescription?.length > 0 ? (
            <Box
              css={css`
            background: ${chakraTokenToVar('bg')};
            border: 2px solid ${chakraTokenToVar('border')};
            border-radius: 8px;
            padding: 16px;
            font-size: 14px;

            ul {
              columns: 200px 2;
              column-gap: 24px;
              list-style: none;
              margin: 0;
              padding-left: 1em;

              li {
                margin-bottom: 8px;
                break-inside: avoid;

                &:before {
                  display: inline-block;
                  content: '-';
                  width: 1em;
                  margin-left: -1em;
                }
              }
            }
          `}
            >
              <Markdown>{topicDescription}</Markdown>
            </Box>
          ) : null}
        </Container>
      </Box>
      <Container className="TopicResults">
        <Tabs<TopicDetailTab>
          currentTab={currentTab}
          tabToUrl={tabToUrl}
          tabs={['Sessions', 'Additional Resources']}
          mb={4}
        />
        {currentTab === 'Sessions' ? (
          <>
            <Stack
              direction="row"
              justify="space-between"
              width="100%"
              align="center"
              mb={4}
              mt={4}
              spacing={0}
              flexWrap="wrap"
            >
              <Heading size="xl" pt={4} pr={4}>
                Small-group help sessions in &quot;{topicName}&quot;
              </Heading>
              <Box pt={4}>
                <Tabs<TopicSessionsDetailTab>
                  variant="rounded"
                  currentTab={currentSessionsTab}
                  tabToUrl={sessionsTabToUrl}
                  tabs={['Upcoming', 'Past']}
                />
              </Box>
            </Stack>
            {sessions.length === 0 ? (
              <>
                <Text mt="32px" mb={0} fontWeight="bold" color="fg.muted">
                  0 Results
                </Text>
                <CourseResultsSingleEmpty type="sessions" context={topicName} hasFilters={false} />
              </>
            ) : (
              <>
                <Box mb={8} mt={6}>
                  Register for a session to review this topic with a small group of learners.
                </Box>
                <SessionList
                  sessions={sessions}
                  totalItemsCount={availableSessionCount ?? 0}
                  hideHeader
                  limit={queryLimit}
                  onLoadMore={handleLoadMore}
                  showAllPath={showAllSessionsPath}
                />
              </>
            )}
          </>
        ) : (
          <>
            <Heading size="xl" mb={4} mt={8}>
              Explore other {courseName} Topics
            </Heading>
            <CourseTopicsList course={course} />
          </>
        )}
      </Container>
      )
    </Layout>
  )
}

const TopicIndexPage = makePage(TopicIndex, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TopicIndexPage)
export default TopicIndexPage
